import 'styles/typekit.module.css'
import { IncomingMessage, ServerResponse } from 'http'
import { slugs } from '@/constants/slugs'
import { ServerExperiments } from '@/experimentation'
import { ExperimentParamsArray, runExperimentsOnServer } from '@/experimentation/server'
import { PresalesLayout } from '@/layouts/PresalesLayout'
import { NextPageWithLayout } from '@/layouts/types'
import { getGiveawayContent } from '@/services/GiveawayService'
import { PageGiveawayContent, resolvePageGiveawayContent } from '@/services/GiveawayService/GiveawayService'
import { getCurrentPhaseSlug, PhaseSlugForTracking } from '@/services/ProjectPhaseService'
import { getProjectAbout, getWatchableContentByGuid } from '@/services/ProjectsService'
import { getShopifyCollection, ShopifyProductWithBundles } from '@/services/Shopify/ShopifyService'
import { getSweepstake } from '@/services/SweepstakeService'
import { getLightTheatricalRelease } from '@/services/Theatrical/queries'
import { SweepstakeObject } from '@/types/codegen-federation'
import { getItemOrFirstEntry } from '@/utils/array-utils'
import { logger } from '@/utils/logging'
import { getServerSidePropsErrorHandler } from '@/utils/nextUtils/nextErrorHandlers'
import { loadTranslations } from '@/utils/translate/translate-server'
import { GiveawayView } from '@/views/PreSales/GiveawayView'
import { getCustomColors } from '@/views/PreSales/GiveawayView/utils/customColors'
import { PreSalesGiveawayView } from '@/views/PreSales/PreSalesGiveawayView'

interface GiveawayPageProps {
  content: PageGiveawayContent | null
  theatricalSlug: string
  tickets: ShopifyProductWithBundles[] | null
  sweepstake: SweepstakeObject
  phase: PhaseSlugForTracking
  experiments?: ServerExperiments
  releaseDate: string | null
}

export const GiveawayPage: NextPageWithLayout<GiveawayPageProps> = ({
  content,
  tickets,
  theatricalSlug,
  phase,
  sweepstake,
  experiments,
  releaseDate,
}) => {
  // If we are getting content from Contentful, use the new GiveawayView component
  if (content) {
    return (
      <div
        className="absolute inset-0 !top-0 !overflow-x-hidden bg-white lg:fixed"
        style={getCustomColors(content.theme)}
      >
        <GiveawayView
          content={content}
          sweepstake={sweepstake}
          theatricalSlug={theatricalSlug}
          tickets={tickets}
          phase={phase}
          experiments={experiments}
          releaseDate={releaseDate}
        />
      </div>
    )
  }

  return (
    <div className="font-compactaCom absolute inset-0 !top-0 !overflow-x-hidden bg-white lg:fixed">
      <PreSalesGiveawayView
        sweepstake={sweepstake}
        theatricalSlug={theatricalSlug}
        tickets={tickets}
        phase={phase}
        experiments={experiments}
        releaseDate={releaseDate}
      />
    </div>
  )
}

// Server-safe list of valid legacy giveaway slugs
const VALID_GIVEAWAY_SLUGS = new Set([
  slugs.kingOfKings,
  slugs.theLastRodeo,
  slugs.sketch,
  slugs.youngWashington,
  slugs.senior,
  slugs.truthAndTreason,
  slugs.david,
  slugs.iWasAStranger,
  slugs.soloMio,
])

export const getServerSideProps = getServerSidePropsErrorHandler<GiveawayPageProps>(
  async ({ federationClient, locale, next404, params, res, req, preview }) => {
    const theatricalSlug = getItemOrFirstEntry(params?.slug)

    if (!theatricalSlug) {
      return next404()
    }

    const [sweepstake, content] = await Promise.all([
      getSweepstake(federationClient, theatricalSlug),
      getGiveawayContent(theatricalSlug, preview),
    ])

    if (!sweepstake || (!content && !VALID_GIVEAWAY_SLUGS.has(theatricalSlug))) {
      return next404()
    }

    const collectionId = sweepstake.bundlesCollectionId

    const [tickets, projectMeta, theatricalRelease, experiments, translations] = await Promise.all([
      collectionId ? await getShopifyCollection({ collectionId }) : Promise.resolve(null),
      getProjectAbout({ slug: theatricalSlug }, federationClient),
      getLightTheatricalRelease({ theatricalSlug, client: federationClient }),
      runGiveawayExperiments({ req, res, theatricalSlug }),
      loadTranslations(locale, ['common', 'home', 'tickets', 'chatbot', 'account']),
    ])

    const watchableContent = projectMeta?.title?.id
      ? await getWatchableContentByGuid(projectMeta.title.id, federationClient)
      : null
    const ticketStatus = theatricalRelease?.region?.ticketsStatus ?? null
    const releaseDate = theatricalRelease?.region?.releaseDate ?? null
    const phase = getCurrentPhaseSlug(watchableContent, ticketStatus)
    const resolvedContent = await resolvePageGiveawayContent(content, req, res)

    return {
      props: {
        tickets: tickets ?? null,
        theatricalSlug,
        phase,
        sweepstake,
        experiments: experiments ?? null,
        content: resolvedContent,
        releaseDate,
        ...translations,
      },
    }
  },
)

async function runGiveawayExperiments({
  req,
  res,
}: {
  req: IncomingMessage & { cookies: Partial<{ [p: string]: string }> }
  res: ServerResponse
  theatricalSlug: string
}): Promise<ServerExperiments> {
  const expConfigs: ExperimentParamsArray = []

  try {
    const experiments = await runExperimentsOnServer(req, res, expConfigs)
    return experiments || {}
  } catch (error) {
    logger().error('Failed to run experiments:', error)
    return expConfigs.reduce((acc, { experimentKey, defaultValue }) => {
      acc[experimentKey] = defaultValue
      return acc
    }, {} as ServerExperiments)
  }
}

GiveawayPage.getLayout = (page) => {
  return <PresalesLayout isDarkMode={false}>{page}</PresalesLayout>
}
export default GiveawayPage
